import { APP_ROUTER, imageTransform } from '@qctsw/common'
import { isClient } from '@qctsw/utils'
import { NuxtLink } from '#components'
import type { CollectiveDetailResponse } from '~/server/types'
import type { TableItem } from '~/shared/types'
import { logicCollectiveDetail } from '@/logic/complaint/collective'
import { useGetRouteParam } from '~/shared/function'
import { COLLECTIVE_SITE_META, SITE_NAME } from '~/shared/site'

export const collectiveStep = {
  childCount: 3,
  componentsList: [
    { title: '无反馈' },
    { title: '有措施，但车主不满意' },
    { title: '最新结果' },
  ],
}

export function useCollectiveDetail() {
  const id = useGetRouteParam('collectiveId', '')
  const { changeMeta } = useSite(COLLECTIVE_SITE_META)
  const { setShareBase } = useShare()

  const { state, detailData, getDetail } = logicCollectiveDetail()
  const { tableData, generateTable } = useCollectivePageTable()
  const { brandId, seriesId, brandList, brandStrs, seriesStrs, seriesComponent, brandListFormat } = useFormatSeriesLs()

  async function initHandler() {
    await getDetail(id.value)

    if (!detailData.value)
      return

    brandListFormat(detailData.value.seriesLs)
    generateTable(detailData.value, seriesComponent)

    // meta
    changeMeta({
      title: `${detailData.value.title} - ${SITE_NAME}`,
      description: detailData.value.complainExplain,
      keywords: [detailData.value.title, ...brandStrs.value?.split('、'), seriesStrs.value?.split('、')].filter(Boolean) as string[],
    })
    if (isClient) {
      // share
      setShareBase({
        url: window.location.href,
        title: detailData.value.title,
        content: detailData.value.complainExplain,
        images: detailData.value.complainImgs.filter(v => !v.match(/^data:.*;base64.*/)).slice(0, 3).map(v => imageTransform(v)),
      })
    }
  }

  return {
    id,
    state,
    detailData,
    tableData,
    brandId,
    seriesId,
    brandList,
    brandStrs,
    seriesStrs,
    brandListFormat,
    initHandler,
  }
}

export function useCollectivePageTable() {
  const tableData = ref<TableItem[]>([])

  function appealFormat(issueLs: CollectiveDetailResponse['issueLs']) {
    if (!issueLs || !issueLs.length)
      return
    const appealList = Array.from(new Set(issueLs.filter(v => v.issueTypeCode === 4)))
    const appealComponents = []
    for (const key in appealList) {
      appealComponents.push(
        h(NuxtLink, {
          class: 'hover:text-blue-5',
          to: APP_ROUTER.complaintSearch({
            appeal: appealList[key].issueId,
          }),
        }, { default: () => appealList[key].label }),
      )
      if (Number.parseInt(key) < appealList.length - 1)
        appealComponents.push(h('span', {}, '、'))
    }
    return appealComponents
  }

  function generateTable(
    res: CollectiveDetailResponse,
    components: {
      brandComponents: VNode[]
      seriesComponents: VNode[]
    },
  ) {
    tableData.value = [
      {
        label: '涉及品牌',
        isShow: !!components.brandComponents.length,
        isComponent: true,
        content: h('div', {}, components.brandComponents),
      },
      {
        label: '涉及车系',
        isShow: !!res.seriesLs.length,
        isComponent: true,
        content: h('div', {}, components.seriesComponents),
      },
      {
        label: '车主诉求',
        isShow: !!res.issueLs.length,
        isComponent: true,
        content: h('div', {}, appealFormat(res.issueLs)),
      },
      {
        label: '说明',
        isShow: !!res.complainExplain,
        content: res.complainExplain,
      },
    ]
  }

  return {
    tableData,
    generateTable,
  }
}

function useFormatSeriesLs() {
  const brandId = ref(0)
  const seriesId = ref(0)
  const brandList = ref<{ brandId: number, brandName: string }[]>([])
  const seriesList = ref<CollectiveDetailResponse['seriesLs']>([])
  const brandStrs = computed(() => {
    const strList = brandList.value.map(v => v.brandName) ?? []
    return strList.join('、')
  })
  const seriesStrs = computed(() => {
    const strList = seriesList.value.map(v => v.seriesName) ?? []
    return strList.join('、')
  })

  const seriesComponent = reactive({
    brandComponents: [] as VNode[],
    seriesComponents: [] as VNode[],
  })

  function brandListFormat(seriesLs: CollectiveDetailResponse['seriesLs']) {
    if (!seriesLs.length)
      return

    seriesList.value = seriesLs
    brandId.value = seriesLs[0].brandId
    seriesId.value = seriesLs[0].seriesId

    for (const i of seriesLs) {
      if (brandList.value.findIndex(v => v.brandId === i.brandId) !== -1)
        continue

      brandList.value.push({
        brandId: i.brandId,
        brandName: i.brandName,
      })
    }

    seriesComponent.brandComponents = []
    for (const key in brandList.value) {
      seriesComponent.brandComponents.push(
        h(NuxtLink, {
          class: 'hover:text-blue-5',
          to: APP_ROUTER.complaintSearch({
            carBrandId: brandList.value[Number(key)].brandId,
            carBrandName: brandList.value[Number(key)].brandName,
          }),
        }, { default: () => brandList.value[Number(key)].brandName }),
      )
      if (Number(key) < brandList.value.length - 1) {
        seriesComponent.brandComponents.push(
          h('span', {}, '、'),
        )
      }
    }

    seriesComponent.seriesComponents = []
    for (const key in seriesLs) {
      seriesComponent.seriesComponents.push(
        h(NuxtLink, {
          class: 'hover:text-blue-5',
          to: APP_ROUTER.complaintSearch({
            carBrandId: brandList.value[0]?.brandId,
            carBrandName: brandList.value[0]?.brandName,
            carSeriesId: seriesLs[key]?.seriesId,
            carSeriesName: seriesLs[key]?.seriesName,
          }),
        }, { default: () => seriesLs[key].seriesName }),
      )

      if (Number(key) < seriesLs.length - 1) {
        seriesComponent.seriesComponents.push(
          h('span', {}, '、'),
        )
      }
    }
  }

  return {
    brandId,
    seriesId,
    brandList,
    brandStrs,
    seriesStrs,
    seriesComponent,
    brandListFormat,
  }
}
